// src/cache/storage/IndexedDB.ts
import { createStore, del, get, getMany, set, setMany } from "idb-keyval";
var IndexedDB = class _IndexedDB {
  static defaultDbName = "fwf";
  static defaultStoreName = "default";
  customStore;
  constructor(dbName = _IndexedDB.defaultDbName, storeName = _IndexedDB.defaultStoreName) {
    this.customStore = createStore(dbName, storeName);
  }
  async get(key) {
    const item = await get(key, this.customStore);
    return item ?? null;
  }
  async getMany(keys) {
    const items = await getMany(keys, this.customStore);
    const results = {};
    keys.forEach((key, index) => {
      results[key] = items[index] ?? null;
    });
    return results;
  }
  async set(key, value) {
    await set(key, value, this.customStore);
  }
  async setMany(items) {
    const entries = Object.entries(items);
    await setMany(entries, this.customStore);
  }
  async delete(key) {
    await del(key, this.customStore);
  }
};

// src/cache/storage/LocalStorage.ts
var LocalStorage = class _LocalStorage {
  static defaultName = "fwf";
  name;
  constructor(name = _LocalStorage.defaultName) {
    this.name = name;
  }
  async get(key) {
    try {
      const item = localStorage.getItem(this.generateKey(key));
      return item ? JSON.parse(item) : null;
    } catch (error) {
      return null;
    }
  }
  async getMany(keys) {
    const results = {};
    keys.forEach((key) => {
      try {
        const item = localStorage.getItem(this.generateKey(key));
        results[key] = item ? JSON.parse(item) : null;
      } catch (error) {
        results[key] = null;
      }
    });
    return results;
  }
  async set(key, value) {
    localStorage.setItem(this.generateKey(key), JSON.stringify(value));
  }
  async setMany(items) {
    Object.entries(items).forEach(([key, value]) => {
      localStorage.setItem(this.generateKey(key), JSON.stringify(value));
    });
  }
  async delete(key) {
    localStorage.removeItem(this.generateKey(key));
  }
  generateKey(key) {
    return `${this.name}:${key}`;
  }
};

// src/cache/Cache.ts
var Cache = class {
  accessToken;
  storage;
  featuresCache = /* @__PURE__ */ new Map();
  holdoutsCache = /* @__PURE__ */ new Map();
  constructor(config) {
    this.accessToken = config.accessToken;
    this.storage = config.storage;
  }
  async getSession(sessionId) {
    const cacheKey = this.generateSessionKey(sessionId);
    return await this.storage.get(cacheKey);
  }
  async setSession(sessionId, data) {
    const cacheKey = this.generateSessionKey(sessionId);
    await this.storage.set(cacheKey, data);
  }
  async setFeatures(features) {
    const featuresToStore = {};
    Object.entries(features).forEach(([key, feature]) => {
      const cacheKey = this.generateFeatureKey(key);
      this.featuresCache.set(cacheKey, feature);
      featuresToStore[cacheKey] = feature;
    });
    await this.storage.setMany(featuresToStore);
  }
  async setLastUpdatedAt() {
    const cacheKey = this.generateLastUpdatedAtKey();
    await this.storage.set(cacheKey, Date.now());
  }
  async setHoldouts(holdouts) {
    const holdoutsToStore = {};
    Object.entries(holdouts).forEach(([key, holdout]) => {
      const cacheKey = this.generateHoldoutKey(key);
      this.holdoutsCache.set(cacheKey, holdout);
      holdoutsToStore[cacheKey] = holdout;
    });
    await this.storage.setMany(holdoutsToStore);
  }
  async setHoldoutKeys(holdoutKeys) {
    const cacheKey = this.generateHoldoutKeysKey();
    await this.storage.set(cacheKey, holdoutKeys);
  }
  async getFeatures(featureKeys) {
    const results = {};
    const missingInCache = [];
    featureKeys.forEach((key) => {
      const cacheKey = this.generateFeatureKey(key);
      const featureInCache = this.featuresCache.get(cacheKey);
      if (featureInCache) {
        results[key] = featureInCache;
      } else {
        missingInCache.push(cacheKey);
      }
    });
    if (!missingInCache.length) {
      return results;
    }
    const featuresInStorage = await this.storage.getMany(missingInCache);
    Object.entries(featuresInStorage).forEach(([cacheKey, feature]) => {
      if (feature) {
        this.featuresCache.set(cacheKey, feature);
        results[feature.key] = feature;
      }
    });
    return results;
  }
  async getFeature(featureKey) {
    const cacheKey = this.generateFeatureKey(featureKey);
    const featureInCache = this.featuresCache.get(cacheKey);
    if (featureInCache) {
      return featureInCache;
    }
    const featureInStorage = await this.storage.get(cacheKey);
    if (featureInStorage) {
      this.featuresCache.set(cacheKey, featureInStorage);
    }
    return featureInStorage;
  }
  async getHoldouts(holdoutKeys) {
    const results = {};
    const missingInCache = [];
    holdoutKeys.forEach((key) => {
      const cacheKey = this.generateHoldoutKey(key);
      const holdoutInCache = this.holdoutsCache.get(cacheKey);
      if (holdoutInCache) {
        results[key] = holdoutInCache;
      } else {
        missingInCache.push(cacheKey);
      }
    });
    if (!missingInCache.length) {
      return results;
    }
    const holdoutsInStorage = await this.storage.getMany(missingInCache);
    Object.entries(holdoutsInStorage).forEach(([cacheKey, holdout]) => {
      if (holdout) {
        this.holdoutsCache.set(cacheKey, holdout);
        results[holdout.key] = holdout;
      }
    });
    return results;
  }
  async getHoldoutKeys() {
    const cacheKey = this.generateHoldoutKeysKey();
    return await this.storage.get(cacheKey);
  }
  async deleteFeature(featureKey) {
    const cacheKey = this.generateFeatureKey(featureKey);
    this.featuresCache.delete(cacheKey);
    await this.storage.delete(cacheKey);
  }
  async getFeaturesToUpdate(featureKeys, debugMode, expirationTime) {
    const featureKeysMap = featureKeys.map((key) => ({ key }));
    if (debugMode) {
      return featureKeysMap;
    }
    const lastUpdatedAt = await this.getLastUpdatedAt();
    if (!lastUpdatedAt) {
      return featureKeysMap;
    }
    const timeToExpire = Date.now() - expirationTime;
    const shouldUpdate = timeToExpire > lastUpdatedAt;
    if (!shouldUpdate) {
      return [];
    }
    const featuresInStorage = await this.getFeatures(featureKeys);
    return Object.values(featuresInStorage).map((feature) => {
      return {
        key: feature.key,
        version: feature.changeVersionControl
      };
    });
  }
  async getLastUpdatedAt() {
    const cacheKey = this.generateLastUpdatedAtKey();
    return await this.storage.get(cacheKey);
  }
  generateFeatureKey(featureKey) {
    return `feature:${featureKey}:${this.accessToken}`;
  }
  generateLastUpdatedAtKey() {
    return `lastUpdatedAt:${this.accessToken}`;
  }
  generateHoldoutKey(holdoutKey) {
    return `holdout:${holdoutKey}:${this.accessToken}`;
  }
  generateHoldoutKeysKey() {
    return `holdoutKeys:${this.accessToken}`;
  }
  generateSessionKey(sessionId) {
    return `session:${this.accessToken}:${sessionId}`;
  }
};

// src/evaluator/EvalResult.ts
var EvalResult = class _EvalResult {
  variation;
  variationName;
  abTest;
  explanation;
  metadata;
  constructor(config) {
    this.variation = config.variation;
    this.variationName = config.variationName;
    this.abTest = config.abTest;
    this.explanation = config.explanation;
    this.metadata = config.metadata;
  }
  static getHoldoutResult(params) {
    if (typeof params.variation === "undefined") {
      return _EvalResult.getInvalidVariationResult(params.fallback);
    }
    return new _EvalResult({
      variation: params.variation,
      variationName: params.variationName ?? params.variation,
      abTest: false,
      explanation: {
        kind: "HOLDOUT",
        evalHoldouts: params.evalHoldouts
      },
      metadata: params.metadata
    });
  }
  static getPrerequisiteFailedResult(params) {
    return new _EvalResult({
      variation: params.variation,
      variationName: params.variationName ?? params.variation,
      abTest: false,
      explanation: {
        kind: "PREREQUISITE_FAILED",
        evalFlags: params.evalFlags,
        evalHoldouts: params.evalHoldouts
      },
      metadata: params.metadata
    });
  }
  static getTargetResult(params) {
    if (typeof params.variation === "undefined") {
      return _EvalResult.getInvalidVariationResult(params.fallback);
    }
    return new _EvalResult({
      variation: params.variation,
      variationName: params.variationName ?? params.variation,
      abTest: false,
      explanation: {
        kind: "TARGET",
        evalAttrs: ["targetId"],
        evalHoldouts: params.evalHoldouts
      },
      metadata: params.metadata
    });
  }
  static getSegmentResult(params) {
    if (typeof params.variation === "undefined") {
      return _EvalResult.getInvalidVariationResult(params.fallback);
    }
    return new _EvalResult({
      variation: params.variation,
      variationName: params.variationName ?? params.variation,
      abTest: false,
      explanation: {
        kind: "SEGMENT",
        evalAttrs: ["targetId"],
        evalHoldouts: params.evalHoldouts
      },
      metadata: params.metadata
    });
  }
  static getRuleResult(params) {
    if (typeof params.variation === "undefined") {
      return _EvalResult.getInvalidVariationResult(params.fallback);
    }
    return new _EvalResult({
      variation: params.variation,
      variationName: params.variationName ?? params.variation,
      abTest: params.abTest,
      explanation: {
        kind: "RULE",
        ruleIdx: params.ruleIdx,
        evalAttrs: params.evalAttrs,
        evalHoldouts: params.evalHoldouts
      },
      metadata: params.metadata
    });
  }
  static getDefaultRuleResult(params) {
    if (typeof params.variation === "undefined") {
      return _EvalResult.getInvalidVariationResult(params.fallback);
    }
    return new _EvalResult({
      variation: params.variation,
      variationName: params.variationName ?? params.variation,
      abTest: params.abTest,
      explanation: {
        kind: "DEFAULT_RULE",
        ruleIdx: 0,
        evalHoldouts: params.evalHoldouts
      },
      metadata: params.metadata
    });
  }
  static getOffVariationResult(params) {
    return new _EvalResult({
      variation: params.variation,
      variationName: params.variationName,
      abTest: false,
      explanation: {
        kind: "OFF_VARIATION",
        evalHoldouts: params.evalHoldouts
      },
      metadata: params.metadata
    });
  }
  static getServerVariationResult(response, fallback) {
    if (!response.explanation || response.explanation.kind === "ERROR") {
      return _EvalResult.getFallbackResult(fallback, response.explanation?.error ?? "Unknown error");
    }
    return new _EvalResult({
      variation: response.variation,
      variationName: response.trackInfo?.variationName ?? response.variation,
      abTest: response.abTest,
      explanation: {
        kind: response.explanation.kind,
        ruleIdx: response.explanation.ruleIdx ?? -1,
        evalAttrs: response.explanation.evalAttrs,
        evalFlags: response.explanation.evalFlags,
        evalHoldouts: response.holdoutsEvaluations ?? void 0
      }
    });
  }
  static getFallbackResult(fallback, error) {
    return new _EvalResult({
      variation: fallback,
      variationName: fallback,
      abTest: false,
      explanation: {
        kind: "FALLBACK",
        error
      }
    });
  }
  static getInvalidVariationResult(fallback) {
    return _EvalResult.getFallbackResult(fallback, "Invalid variation");
  }
};

// src/utils/proto.ts
function mapValue(value) {
  switch (value.kind.oneofKind) {
    case "nullValue":
      return null;
    case "boolValue":
      return value.kind.boolValue;
    case "stringValue":
      return value.kind.stringValue;
    case "numberValue":
      return value.kind.numberValue;
    case "listValue":
      return value.kind.listValue.values.map((value2) => mapValue(value2));
    case "structValue":
      return Object.fromEntries(
        Object.entries(value.kind.structValue.fields).map(([key, value2]) => [key, mapValue(value2)])
      );
    default:
      throw new Error("Unknown kind");
  }
}
function mapTimestamp(timestamp) {
  const secondsToMs = timestamp.seconds * 1e3;
  const nanosToMs = timestamp.nanos / 1e6;
  return new Date(secondsToMs + nanosToMs);
}

// src/utils/allocation.ts
async function digestMessage(message) {
  const msgUint8 = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest("SHA-1", msgUint8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  return hashHex;
}
async function getBucketAllocation(key) {
  const digest = await digestMessage(key);
  const shortDigest = digest.slice(0, 15);
  const decimalDigest = parseInt(shortDigest, 16);
  return decimalDigest / 1152921504606847e3;
}
function getUserBucket(featureKey, salt, identifier) {
  return getBucketAllocation(`${featureKey}.${salt}.${identifier}`);
}
function getHoldoutBucket(holdoutKey, account, identifier) {
  return getBucketAllocation(`${holdoutKey}.${account}.${identifier}`);
}

// src/evaluator/Operation.ts
import { compare } from "compare-versions";

// src/utils/invariant.ts
function isNumber(value) {
  return typeof value === "number";
}
function isNumberArray(value) {
  return value.every(isNumber);
}
function isString(value) {
  return typeof value === "string";
}
function isStringArray(value) {
  return value.every(isString);
}
function invariant(condition, message) {
  if (!condition) {
    throw new Error(message);
  }
}

// src/evaluator/Operation.ts
var OPERATORS = {
  IN: "in",
  IS: "is",
  STARTS_WITH: "startsWith",
  ENDS_WITH: "endsWith",
  MATCHES: "matches",
  CONTAINS: "contains",
  LESS_THAN: "lessThan",
  LESS_THAN_OR_EQUAL: "lessThanOrEqual",
  GREATER_THAN: "greaterThan",
  GREATER_THAN_OR_EQUAL: "greaterThanOrEqual",
  LESS_THAN_VERSION: "lessThanVersion",
  LESS_THAN_OR_EQUAL_VERSION: "lessThanOrEqualVersion",
  GREATER_THAN_VERSION: "greaterThanVersion",
  GREATER_THAN_OR_EQUAL_VERSION: "greaterThanOrEqualVersion"
};
var Operation = class _Operation {
  static get(operator) {
    switch (operator) {
      case OPERATORS.IN:
        return _Operation.in;
      case OPERATORS.IS:
        return _Operation.is;
      case OPERATORS.STARTS_WITH:
        return _Operation.startsWith;
      case OPERATORS.ENDS_WITH:
        return _Operation.endsWith;
      case OPERATORS.MATCHES:
        return _Operation.matches;
      case OPERATORS.CONTAINS:
        return _Operation.contains;
      case OPERATORS.LESS_THAN:
        return _Operation.lessThan;
      case OPERATORS.LESS_THAN_OR_EQUAL:
        return _Operation.lessThanOrEqual;
      case OPERATORS.GREATER_THAN:
        return _Operation.greaterThan;
      case OPERATORS.GREATER_THAN_OR_EQUAL:
        return _Operation.greaterThanOrEqual;
      case OPERATORS.LESS_THAN_VERSION:
        return _Operation.lessThanVersion;
      case OPERATORS.LESS_THAN_OR_EQUAL_VERSION:
        return _Operation.lessThanOrEqualVersion;
      case OPERATORS.GREATER_THAN_VERSION:
        return _Operation.greaterThanVersion;
      case OPERATORS.GREATER_THAN_OR_EQUAL_VERSION:
        return _Operation.greaterThanOrEqualVersion;
      default:
        return false;
    }
  }
  static in(target, values) {
    return values.includes(target);
  }
  static is(target, values) {
    return values.includes(target);
  }
  static startsWith(target, values) {
    try {
      invariant(isString(target), "Expected a string");
      invariant(isStringArray(values), "Expected a string array");
      return values.some((value) => target.startsWith(value));
    } catch (error) {
      return false;
    }
  }
  static endsWith(target, values) {
    try {
      invariant(isString(target), "Expected a string");
      invariant(isStringArray(values), "Expected a string array");
      return values.some((value) => target.endsWith(value));
    } catch (error) {
      return false;
    }
  }
  static matches(target, values) {
    try {
      invariant(isString(target), "Expected a string");
      invariant(isStringArray(values), "Expected a string array");
      return values.some((value) => new RegExp(value).test(target));
    } catch (error) {
      return false;
    }
  }
  static contains(target, values) {
    try {
      invariant(isString(target), "Expected a string");
      invariant(isStringArray(values), "Expected a string array");
      return values.some((value) => target.includes(value));
    } catch (error) {
      return false;
    }
  }
  static lessThan(target, values) {
    try {
      invariant(isNumber(target), "Expected a number");
      invariant(isNumberArray(values), "Expected a number array");
      return values.some((value) => target < value);
    } catch (error) {
      return false;
    }
  }
  static lessThanOrEqual(target, values) {
    try {
      invariant(isNumber(target), "Expected a number");
      invariant(isNumberArray(values), "Expected a number array");
      return values.some((value) => target <= value);
    } catch (error) {
      return false;
    }
  }
  static greaterThan(target, values) {
    try {
      invariant(isNumber(target), "Expected a number");
      invariant(isNumberArray(values), "Expected a number array");
      return values.some((value) => target > value);
    } catch (error) {
      return false;
    }
  }
  static greaterThanOrEqual(target, values) {
    try {
      invariant(isNumber(target), "Expected a number");
      invariant(isNumberArray(values), "Expected a number array");
      return values.some((value) => target >= value);
    } catch (error) {
      return false;
    }
  }
  static lessThanVersion(target, values) {
    try {
      invariant(isString(target), "Expected a string");
      invariant(isStringArray(values), "Expected a string array");
      return values.some((value) => compare(target, value, "<"));
    } catch (error) {
      return false;
    }
  }
  static lessThanOrEqualVersion(target, values) {
    try {
      invariant(isString(target), "Expected a string");
      invariant(isStringArray(values), "Expected a string array");
      return values.some((value) => compare(target, value, "<="));
    } catch (error) {
      return false;
    }
  }
  static greaterThanVersion(target, values) {
    try {
      invariant(isString(target), "Expected a string");
      invariant(isStringArray(values), "Expected a string array");
      return values.some((value) => compare(target, value, ">"));
    } catch (error) {
      return false;
    }
  }
  static greaterThanOrEqualVersion(target, values) {
    try {
      invariant(isString(target), "Expected a string");
      invariant(isStringArray(values), "Expected a string array");
      return values.some((value) => compare(target, value, ">="));
    } catch (error) {
      return false;
    }
  }
};

// src/evaluator/Clause.ts
var Clause = class _Clause {
  attribute;
  operator;
  negate;
  values;
  constructor(config) {
    this.attribute = config.attribute;
    this.operator = config.operator;
    this.negate = config.negate;
    this.values = config.values;
  }
  static fromMessage(clause) {
    return new _Clause({
      attribute: clause.attribute,
      operator: clause.operator,
      negate: clause.negate,
      values: clause.values.map((value) => mapValue(value))
    });
  }
  evaluate(targets) {
    const target = targets[this.attribute];
    if (!target) {
      return false;
    }
    const operation = Operation.get(this.operator);
    if (!operation) {
      return false;
    }
    const result = operation(target, this.values);
    return this.negate ? !result : result;
  }
};

// src/evaluator/Holdout.ts
var Holdout = class _Holdout {
  key;
  account;
  clauses;
  endDate;
  allocation;
  constructor(config) {
    this.key = config.key;
    this.account = config.account;
    this.clauses = config.clauses;
    this.endDate = config.endDate;
    this.allocation = config.allocation;
  }
  static fromMessage(holdout) {
    return new _Holdout({
      key: holdout.key,
      account: holdout.account,
      clauses: holdout.clauses.map((clause) => Clause.fromMessage(clause)),
      endDate: holdout.endDate ? mapTimestamp(holdout.endDate) : void 0,
      allocation: holdout.allocation
    });
  }
  evaluate(targets) {
    const isRunning = this.endDate ? this.endDate > /* @__PURE__ */ new Date() : false;
    return isRunning && this.clauses.every((clause) => clause.evaluate(targets));
  }
  async isHoldoutUser(userId) {
    const holdoutBucket = await getHoldoutBucket(this.key, this.account, userId);
    const totalWeight = this.allocation / 1e5;
    return holdoutBucket < totalWeight;
  }
};

// src/proto/generated/features_pb.ts
import { MessageType as MessageType3 } from "@protobuf-ts/runtime";

// src/proto/generated/google/protobuf/timestamp_pb.ts
import { typeofJsonValue } from "@protobuf-ts/runtime";
import { PbLong } from "@protobuf-ts/runtime";
import { MessageType } from "@protobuf-ts/runtime";
var Timestamp$Type = class extends MessageType {
  constructor() {
    super("google.protobuf.Timestamp", [
      {
        no: 1,
        name: "seconds",
        kind: "scalar",
        T: 3,
        L: 2
        /*LongType.NUMBER*/
      },
      {
        no: 2,
        name: "nanos",
        kind: "scalar",
        T: 5
        /*ScalarType.INT32*/
      }
    ]);
  }
  /**
   * Creates a new `Timestamp` for the current time.
   */
  now() {
    const msg = this.create();
    const ms = Date.now();
    msg.seconds = PbLong.from(Math.floor(ms / 1e3)).toNumber();
    msg.nanos = ms % 1e3 * 1e6;
    return msg;
  }
  /**
   * Converts a `Timestamp` to a JavaScript Date.
   */
  toDate(message) {
    return new Date(PbLong.from(message.seconds).toNumber() * 1e3 + Math.ceil(message.nanos / 1e6));
  }
  /**
   * Converts a JavaScript Date to a `Timestamp`.
   */
  fromDate(date) {
    const msg = this.create();
    const ms = date.getTime();
    msg.seconds = PbLong.from(Math.floor(ms / 1e3)).toNumber();
    msg.nanos = ms % 1e3 * 1e6;
    return msg;
  }
  /**
   * In JSON format, the `Timestamp` type is encoded as a string
   * in the RFC 3339 format.
   */
  internalJsonWrite(message, options) {
    let ms = PbLong.from(message.seconds).toNumber() * 1e3;
    if (ms < Date.parse("0001-01-01T00:00:00Z") || ms > Date.parse("9999-12-31T23:59:59Z"))
      throw new Error("Unable to encode Timestamp to JSON. Must be from 0001-01-01T00:00:00Z to 9999-12-31T23:59:59Z inclusive.");
    if (message.nanos < 0)
      throw new Error("Unable to encode invalid Timestamp to JSON. Nanos must not be negative.");
    let z = "Z";
    if (message.nanos > 0) {
      let nanosStr = (message.nanos + 1e9).toString().substring(1);
      if (nanosStr.substring(3) === "000000")
        z = "." + nanosStr.substring(0, 3) + "Z";
      else if (nanosStr.substring(6) === "000")
        z = "." + nanosStr.substring(0, 6) + "Z";
      else
        z = "." + nanosStr + "Z";
    }
    return new Date(ms).toISOString().replace(".000Z", z);
  }
  /**
   * In JSON format, the `Timestamp` type is encoded as a string
   * in the RFC 3339 format.
   */
  internalJsonRead(json, options, target) {
    if (typeof json !== "string")
      throw new Error("Unable to parse Timestamp from JSON " + typeofJsonValue(json) + ".");
    let matches = json.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})T([0-9]{2}):([0-9]{2}):([0-9]{2})(?:Z|\.([0-9]{3,9})Z|([+-][0-9][0-9]:[0-9][0-9]))$/);
    if (!matches)
      throw new Error("Unable to parse Timestamp from JSON. Invalid format.");
    let ms = Date.parse(matches[1] + "-" + matches[2] + "-" + matches[3] + "T" + matches[4] + ":" + matches[5] + ":" + matches[6] + (matches[8] ? matches[8] : "Z"));
    if (Number.isNaN(ms))
      throw new Error("Unable to parse Timestamp from JSON. Invalid value.");
    if (ms < Date.parse("0001-01-01T00:00:00Z") || ms > Date.parse("9999-12-31T23:59:59Z"))
      throw new globalThis.Error("Unable to parse Timestamp from JSON. Must be from 0001-01-01T00:00:00Z to 9999-12-31T23:59:59Z inclusive.");
    if (!target)
      target = this.create();
    target.seconds = PbLong.from(ms / 1e3).toNumber();
    target.nanos = 0;
    if (matches[7])
      target.nanos = parseInt("1" + matches[7] + "0".repeat(9 - matches[7].length)) - 1e9;
    return target;
  }
};
var Timestamp = new Timestamp$Type();

// src/proto/generated/google/protobuf/struct_pb.ts
import { isJsonObject } from "@protobuf-ts/runtime";
import { typeofJsonValue as typeofJsonValue2 } from "@protobuf-ts/runtime";
import { MessageType as MessageType2 } from "@protobuf-ts/runtime";
var NullValue = /* @__PURE__ */ ((NullValue2) => {
  NullValue2[NullValue2["NULL_VALUE"] = 0] = "NULL_VALUE";
  return NullValue2;
})(NullValue || {});
var Struct$Type = class extends MessageType2 {
  constructor() {
    super("google.protobuf.Struct", [
      { no: 1, name: "fields", kind: "map", K: 9, V: { kind: "message", T: () => Value } }
    ]);
  }
  /**
   * Encode `Struct` to JSON object.
   */
  internalJsonWrite(message, options) {
    let json = {};
    for (let [k, v] of Object.entries(message.fields)) {
      json[k] = Value.toJson(v);
    }
    return json;
  }
  /**
   * Decode `Struct` from JSON object.
   */
  internalJsonRead(json, options, target) {
    if (!isJsonObject(json))
      throw new globalThis.Error("Unable to parse message " + this.typeName + " from JSON " + typeofJsonValue2(json) + ".");
    if (!target)
      target = this.create();
    for (let [k, v] of globalThis.Object.entries(json)) {
      target.fields[k] = Value.fromJson(v);
    }
    return target;
  }
};
var Struct = new Struct$Type();
var Value$Type = class extends MessageType2 {
  constructor() {
    super("google.protobuf.Value", [
      { no: 1, name: "null_value", kind: "enum", oneof: "kind", T: () => ["google.protobuf.NullValue", NullValue] },
      {
        no: 2,
        name: "number_value",
        kind: "scalar",
        oneof: "kind",
        T: 1
        /*ScalarType.DOUBLE*/
      },
      {
        no: 3,
        name: "string_value",
        kind: "scalar",
        oneof: "kind",
        T: 9
        /*ScalarType.STRING*/
      },
      {
        no: 4,
        name: "bool_value",
        kind: "scalar",
        oneof: "kind",
        T: 8
        /*ScalarType.BOOL*/
      },
      { no: 5, name: "struct_value", kind: "message", oneof: "kind", T: () => Struct },
      { no: 6, name: "list_value", kind: "message", oneof: "kind", T: () => ListValue }
    ]);
  }
  /**
   * Encode `Value` to JSON value.
   */
  internalJsonWrite(message, options) {
    if (message.kind.oneofKind === void 0)
      throw new globalThis.Error();
    switch (message.kind.oneofKind) {
      case void 0:
        throw new globalThis.Error();
      case "boolValue":
        return message.kind.boolValue;
      case "nullValue":
        return null;
      case "numberValue":
        let numberValue = message.kind.numberValue;
        if (typeof numberValue == "number" && !Number.isFinite(numberValue))
          throw new globalThis.Error();
        return numberValue;
      case "stringValue":
        return message.kind.stringValue;
      case "listValue":
        let listValueField = this.fields.find((f) => f.no === 6);
        if (listValueField?.kind !== "message")
          throw new globalThis.Error();
        return listValueField.T().toJson(message.kind.listValue);
      case "structValue":
        let structValueField = this.fields.find((f) => f.no === 5);
        if (structValueField?.kind !== "message")
          throw new globalThis.Error();
        return structValueField.T().toJson(message.kind.structValue);
    }
  }
  /**
   * Decode `Value` from JSON value.
   */
  internalJsonRead(json, options, target) {
    if (!target)
      target = this.create();
    switch (typeof json) {
      case "number":
        target.kind = { oneofKind: "numberValue", numberValue: json };
        break;
      case "string":
        target.kind = { oneofKind: "stringValue", stringValue: json };
        break;
      case "boolean":
        target.kind = { oneofKind: "boolValue", boolValue: json };
        break;
      case "object":
        if (json === null) {
          target.kind = { oneofKind: "nullValue", nullValue: 0 /* NULL_VALUE */ };
        } else if (globalThis.Array.isArray(json)) {
          target.kind = { oneofKind: "listValue", listValue: ListValue.fromJson(json) };
        } else {
          target.kind = { oneofKind: "structValue", structValue: Struct.fromJson(json) };
        }
        break;
      default:
        throw new globalThis.Error("Unable to parse " + this.typeName + " from JSON " + typeofJsonValue2(json));
    }
    return target;
  }
};
var Value = new Value$Type();
var ListValue$Type = class extends MessageType2 {
  constructor() {
    super("google.protobuf.ListValue", [
      { no: 1, name: "values", kind: "message", repeat: 1, T: () => Value }
    ]);
  }
  /**
   * Encode `ListValue` to JSON array.
   */
  internalJsonWrite(message, options) {
    return message.values.map((v) => Value.toJson(v));
  }
  /**
   * Decode `ListValue` from JSON array.
   */
  internalJsonRead(json, options, target) {
    if (!globalThis.Array.isArray(json))
      throw new globalThis.Error("Unable to parse " + this.typeName + " from JSON " + typeofJsonValue2(json));
    if (!target)
      target = this.create();
    let values = json.map((v) => Value.fromJson(v));
    target.values.push(...values);
    return target;
  }
};
var ListValue = new ListValue$Type();

// src/proto/generated/features_pb.ts
var Participants = /* @__PURE__ */ ((Participants2) => {
  Participants2[Participants2["UNSPECIFIED"] = 0] = "UNSPECIFIED";
  Participants2[Participants2["ALL"] = 1] = "ALL";
  Participants2[Participants2["ONLY_PARTICIPANTS"] = 2] = "ONLY_PARTICIPANTS";
  Participants2[Participants2["ONLY_NON_PARTICIPANTS"] = 3] = "ONLY_NON_PARTICIPANTS";
  return Participants2;
})(Participants || {});
var ErrorCode = /* @__PURE__ */ ((ErrorCode2) => {
  ErrorCode2[ErrorCode2["ERROR_UNSPECIFIED"] = 0] = "ERROR_UNSPECIFIED";
  ErrorCode2[ErrorCode2["ERROR_FEATURE_NOT_FOUND"] = 1] = "ERROR_FEATURE_NOT_FOUND";
  ErrorCode2[ErrorCode2["ERROR_FEATURE_ARCHIVED"] = 2] = "ERROR_FEATURE_ARCHIVED";
  ErrorCode2[ErrorCode2["ERROR_UNABLE_TO_PROCESS_FLAG"] = 3] = "ERROR_UNABLE_TO_PROCESS_FLAG";
  return ErrorCode2;
})(ErrorCode || {});
var ClauseMsg$Type = class extends MessageType3 {
  constructor() {
    super("protobuf.ClauseMsg", [
      {
        no: 1,
        name: "attribute",
        kind: "scalar",
        T: 9
        /*ScalarType.STRING*/
      },
      {
        no: 2,
        name: "operator",
        kind: "scalar",
        T: 9
        /*ScalarType.STRING*/
      },
      { no: 3, name: "values", kind: "message", repeat: 1, T: () => Value },
      {
        no: 4,
        name: "negate",
        kind: "scalar",
        T: 8
        /*ScalarType.BOOL*/
      }
    ]);
  }
};
var ClauseMsg = new ClauseMsg$Type();
var RolloutMsg$Type = class extends MessageType3 {
  constructor() {
    super("protobuf.RolloutMsg", [
      { no: 1, name: "variation", kind: "message", T: () => Value },
      { no: 2, name: "variation_name", kind: "message", T: () => Value },
      {
        no: 3,
        name: "weight",
        kind: "scalar",
        T: 3,
        L: 2
        /*LongType.NUMBER*/
      },
      {
        no: 4,
        name: "is_participating",
        kind: "scalar",
        T: 8
        /*ScalarType.BOOL*/
      }
    ]);
  }
};
var RolloutMsg = new RolloutMsg$Type();
var RuleMsg$Type = class extends MessageType3 {
  constructor() {
    super("protobuf.RuleMsg", [
      { no: 1, name: "clauses", kind: "message", repeat: 1, T: () => ClauseMsg },
      {
        no: 2,
        name: "default",
        kind: "scalar",
        T: 8
        /*ScalarType.BOOL*/
      },
      { no: 3, name: "variation", kind: "message", T: () => Value },
      { no: 4, name: "variation_name", kind: "message", T: () => Value },
      { no: 5, name: "rollouts", kind: "message", repeat: 1, T: () => RolloutMsg }
    ]);
  }
};
var RuleMsg = new RuleMsg$Type();
var TestTargetMsg$Type = class extends MessageType3 {
  constructor() {
    super("protobuf.TestTargetMsg", [
      { no: 1, name: "variation", kind: "message", T: () => Value },
      { no: 2, name: "variation_name", kind: "message", T: () => Value },
      {
        no: 3,
        name: "values",
        kind: "scalar",
        repeat: 2,
        T: 9
        /*ScalarType.STRING*/
      }
    ]);
  }
};
var TestTargetMsg = new TestTargetMsg$Type();
var PrerequisiteMsg$Type = class extends MessageType3 {
  constructor() {
    super("protobuf.PrerequisiteMsg", [
      { no: 1, name: "expected_variation", kind: "message", T: () => Value },
      { no: 2, name: "participants", kind: "enum", T: () => ["protobuf.Participants", Participants, "PARTICIPANTS_"] },
      {
        no: 3,
        name: "feature_key",
        kind: "scalar",
        T: 9
        /*ScalarType.STRING*/
      }
    ]);
  }
};
var PrerequisiteMsg = new PrerequisiteMsg$Type();
var HoldoutMsg$Type = class extends MessageType3 {
  constructor() {
    super("protobuf.HoldoutMsg", [
      {
        no: 1,
        name: "key",
        kind: "scalar",
        T: 9
        /*ScalarType.STRING*/
      },
      {
        no: 2,
        name: "allocation",
        kind: "scalar",
        T: 3,
        L: 2
        /*LongType.NUMBER*/
      },
      { no: 3, name: "clauses", kind: "message", repeat: 1, T: () => ClauseMsg },
      { no: 4, name: "endDate", kind: "message", T: () => Timestamp },
      {
        no: 5,
        name: "account",
        kind: "scalar",
        T: 9
        /*ScalarType.STRING*/
      }
    ]);
  }
};
var HoldoutMsg = new HoldoutMsg$Type();
var FeatureMsg$Type = class extends MessageType3 {
  constructor() {
    super("protobuf.FeatureMsg", [
      {
        no: 1,
        name: "key",
        kind: "scalar",
        T: 9
        /*ScalarType.STRING*/
      },
      {
        no: 2,
        name: "kind",
        kind: "scalar",
        T: 9
        /*ScalarType.STRING*/
      },
      {
        no: 3,
        name: "enabled",
        kind: "scalar",
        T: 8
        /*ScalarType.BOOL*/
      },
      {
        no: 4,
        name: "eval_userID",
        kind: "scalar",
        T: 8
        /*ScalarType.BOOL*/
      },
      {
        no: 5,
        name: "salt",
        kind: "scalar",
        T: 9
        /*ScalarType.STRING*/
      },
      { no: 6, name: "default_rule", kind: "message", T: () => RuleMsg },
      { no: 7, name: "off_variation", kind: "message", T: () => Value },
      { no: 8, name: "off_variation_name", kind: "message", T: () => Value },
      { no: 9, name: "rules", kind: "message", repeat: 1, T: () => RuleMsg },
      { no: 10, name: "segments", kind: "message", repeat: 1, T: () => TestTargetMsg },
      { no: 11, name: "targets", kind: "message", repeat: 1, T: () => TestTargetMsg },
      { no: 12, name: "prerequisite_features", kind: "message", repeat: 1, T: () => PrerequisiteMsg },
      {
        no: 13,
        name: "change_version_control",
        kind: "scalar",
        T: 3,
        L: 2
        /*LongType.NUMBER*/
      },
      {
        no: 14,
        name: "identifiers_validation_enabled",
        kind: "scalar",
        T: 8
        /*ScalarType.BOOL*/
      },
      {
        no: 15,
        name: "holdout_keys",
        kind: "scalar",
        repeat: 2,
        T: 9
        /*ScalarType.STRING*/
      },
      { no: 16, name: "holdout_variation", kind: "message", T: () => Value },
      { no: 17, name: "holdout_variation_name", kind: "message", T: () => Value }
    ]);
  }
};
var FeatureMsg = new FeatureMsg$Type();
var ErrorMsg$Type = class extends MessageType3 {
  constructor() {
    super("protobuf.ErrorMsg", [
      {
        no: 1,
        name: "message",
        kind: "scalar",
        T: 9
        /*ScalarType.STRING*/
      },
      { no: 2, name: "error_code", kind: "enum", T: () => ["protobuf.ErrorCode", ErrorCode] }
    ]);
  }
};
var ErrorMsg = new ErrorMsg$Type();
var FeatureOrErrorMsg$Type = class extends MessageType3 {
  constructor() {
    super("protobuf.FeatureOrErrorMsg", [
      { no: 1, name: "feature", kind: "message", T: () => FeatureMsg },
      { no: 2, name: "error", kind: "message", T: () => ErrorMsg }
    ]);
  }
};
var FeatureOrErrorMsg = new FeatureOrErrorMsg$Type();
var FeaturesMsg$Type = class extends MessageType3 {
  constructor() {
    super("protobuf.FeaturesMsg", [
      { no: 1, name: "features", kind: "map", K: 9, V: { kind: "message", T: () => FeatureOrErrorMsg } },
      { no: 2, name: "holdouts", kind: "map", K: 9, V: { kind: "message", T: () => HoldoutMsg } }
    ]);
  }
};
var FeaturesMsg = new FeaturesMsg$Type();

// src/evaluator/Prerequisite.ts
var Prerequisite = class _Prerequisite {
  feature;
  participants;
  expectedVariation;
  constructor(config) {
    this.feature = config.feature;
    this.participants = config.participants;
    this.expectedVariation = config.expectedVariation;
  }
  static fromMessage(prerequisite, feature) {
    return new _Prerequisite({
      feature,
      participants: prerequisite.participants,
      expectedVariation: prerequisite.expectedVariation ? mapValue(prerequisite.expectedVariation) : void 0
    });
  }
  async evaluate(user, fallback) {
    const evalResult = await this.feature.evaluate(user, fallback);
    if (evalResult.explanation.kind === "FALLBACK") {
      return false;
    }
    if (evalResult.variation !== this.expectedVariation) {
      return false;
    }
    if (this.feature.isExperiment()) {
      if (this.participants === 2 /* ONLY_PARTICIPANTS */) {
        return evalResult.abTest;
      }
      if (this.participants === 3 /* ONLY_NON_PARTICIPANTS */) {
        return !evalResult.abTest;
      }
    }
    return true;
  }
};

// src/evaluator/Rollout.ts
var Rollout = class _Rollout {
  variation;
  variationName;
  weight;
  isParticipating;
  constructor(config) {
    this.variation = config.variation;
    this.variationName = config.variationName;
    this.weight = config.weight;
    this.isParticipating = config.isParticipating;
  }
  static fromMessage(rollout) {
    return new _Rollout({
      variation: rollout.variation ? mapValue(rollout.variation) : void 0,
      variationName: rollout.variationName ? mapValue(rollout.variationName) : void 0,
      weight: rollout.weight,
      isParticipating: rollout.isParticipating
    });
  }
};

// src/evaluator/Rule.ts
var Rule = class _Rule {
  variation;
  variationName;
  default;
  clauses;
  rollouts;
  constructor(config) {
    this.variation = config.variation;
    this.variationName = config.variationName;
    this.default = config.default;
    this.clauses = config.clauses;
    this.rollouts = config.rollouts;
  }
  static fromMessage(rule) {
    return new _Rule({
      variation: rule.variation ? mapValue(rule.variation) : void 0,
      variationName: rule.variationName ? mapValue(rule.variationName) : void 0,
      default: rule.default,
      clauses: rule.clauses.map((clause) => Clause.fromMessage(clause)),
      rollouts: rule.rollouts.map((rollout) => Rollout.fromMessage(rollout))
    });
  }
  evaluate(targets) {
    return this.clauses.every((clause) => clause.evaluate(targets));
  }
  getEvalAttributes() {
    return this.clauses.map((clause) => clause.attribute);
  }
  async getRuleResult(featureKey, salt, identifier) {
    if (this.rollouts.length) {
      const userBucket = await getUserBucket(featureKey, salt, identifier);
      let totalSum = 0;
      for (const rollout of this.rollouts) {
        totalSum += rollout.weight / 1e5;
        if (userBucket < totalSum) {
          return {
            variation: rollout.variation,
            variationName: rollout.variationName,
            abTest: rollout.isParticipating
          };
        }
      }
    }
    return {
      variation: this.variation,
      variationName: this.variationName,
      abTest: false
    };
  }
};

// src/evaluator/TestTarget.ts
var TestTarget = class _TestTarget {
  variation;
  variationName;
  values;
  constructor(config) {
    this.variation = config.variation;
    this.variationName = config.variationName;
    this.values = config.values;
  }
  static fromMessage(testTarget) {
    return new _TestTarget({
      variation: testTarget.variation ? mapValue(testTarget.variation) : void 0,
      variationName: testTarget.variationName ? mapValue(testTarget.variationName) : void 0,
      values: testTarget.values
    });
  }
  evaluate(user) {
    return this.values.includes(user.authUserId ?? "") || this.values.includes(user.unAuthUserId ?? "") || this.values.includes(user.email ?? "");
  }
};

// src/evaluator/Feature.ts
var Feature = class _Feature {
  key;
  kind;
  enabled;
  evalByUserId;
  salt;
  holdouts;
  holdoutVariation;
  holdoutVariationName;
  prerequisiteFeatures;
  targets;
  segments;
  rules;
  defaultRule;
  offVariation;
  offVariationName;
  changeVersionControl;
  constructor(config) {
    this.key = config.key;
    this.kind = config.kind;
    this.enabled = config.enabled;
    this.evalByUserId = config.evalByUserId;
    this.salt = config.salt;
    this.holdouts = config.holdouts;
    this.holdoutVariation = config.holdoutVariation;
    this.holdoutVariationName = config.holdoutVariationName;
    this.prerequisiteFeatures = config.prerequisiteFeatures;
    this.targets = config.targets;
    this.segments = config.segments;
    this.rules = config.rules;
    this.defaultRule = config.defaultRule;
    this.offVariation = config.offVariation;
    this.offVariationName = config.offVariationName;
    this.changeVersionControl = config.changeVersionControl;
  }
  static async fromMessage(feature, cache) {
    const holdoutsInCache = await cache.getHoldouts(feature.holdoutKeys);
    const holdouts = [];
    for (const holdoutKey of feature.holdoutKeys) {
      const holdoutInCache = holdoutsInCache[holdoutKey];
      if (holdoutInCache) {
        holdouts.push(Holdout.fromMessage(holdoutInCache));
      }
    }
    const prerequisitesKeys = feature.prerequisiteFeatures.map((prerequisite) => prerequisite.featureKey);
    const prerequisitesInCache = await cache.getFeatures(prerequisitesKeys);
    const prerequisiteFeatures = [];
    for (const prerequisite of feature.prerequisiteFeatures) {
      const featureInCache = prerequisitesInCache[prerequisite.featureKey];
      if (featureInCache) {
        const feature2 = await _Feature.fromMessage(featureInCache, cache);
        prerequisiteFeatures.push(Prerequisite.fromMessage(prerequisite, feature2));
      }
    }
    return new _Feature({
      holdouts,
      prerequisiteFeatures,
      key: feature.key,
      kind: feature.kind,
      enabled: feature.enabled,
      evalByUserId: feature.evalUserID,
      salt: feature.salt,
      holdoutVariation: feature.holdoutVariation,
      holdoutVariationName: feature.holdoutVariationName,
      targets: feature.targets.map((target) => TestTarget.fromMessage(target)),
      segments: feature.segments.map((segment) => TestTarget.fromMessage(segment)),
      rules: feature.rules.map((rule) => Rule.fromMessage(rule)),
      defaultRule: feature.defaultRule ? Rule.fromMessage(feature.defaultRule) : void 0,
      offVariation: feature.offVariation ? mapValue(feature.offVariation) : void 0,
      offVariationName: feature.offVariationName ? mapValue(feature.offVariationName) : void 0,
      changeVersionControl: feature.changeVersionControl
    });
  }
  async evaluate(user, fallback) {
    const metadata = {
      featureKind: this.key,
      featureVersion: this.changeVersionControl
    };
    const evalHoldouts = {};
    if (user.authUserId) {
      for (const holdout of this.holdouts) {
        if (holdout.evaluate(user.custom)) {
          evalHoldouts[holdout.key] = await holdout.isHoldoutUser(user.authUserId);
        }
      }
      for (const evalHoldout in evalHoldouts) {
        if (evalHoldouts[evalHoldout]) {
          return EvalResult.getHoldoutResult({
            variation: this.holdoutVariation,
            variationName: this.holdoutVariationName,
            evalHoldouts,
            fallback,
            metadata
          });
        }
      }
    }
    if (!this.enabled) {
      return EvalResult.getOffVariationResult({
        variation: this.offVariation,
        variationName: this.offVariationName,
        evalHoldouts,
        metadata
      });
    }
    for (const prerequisite of this.prerequisiteFeatures) {
      if (!await prerequisite.evaluate(user, fallback)) {
        return EvalResult.getPrerequisiteFailedResult({
          variation: this.offVariation,
          variationName: this.offVariationName,
          evalFlags: this.prerequisiteFeatures.map((prerequisite2) => prerequisite2.feature.key),
          evalHoldouts,
          metadata
        });
      }
    }
    for (const target of this.targets) {
      if (target.evaluate(user)) {
        return EvalResult.getTargetResult({
          variation: target.variation,
          variationName: target.variationName,
          evalHoldouts,
          fallback,
          metadata
        });
      }
    }
    for (const segment of this.segments) {
      if (segment.evaluate(user)) {
        return EvalResult.getSegmentResult({
          variation: segment.variation,
          variationName: segment.variationName,
          evalHoldouts,
          fallback,
          metadata
        });
      }
    }
    for (const rule of this.rules) {
      if (rule.evaluate(user.custom)) {
        const { abTest, variation, variationName } = await rule.getRuleResult(
          this.key,
          this.salt,
          user.getIdentifier(this.evalByUserId)
        );
        return EvalResult.getRuleResult({
          variation,
          variationName,
          abTest,
          ruleIdx: this.rules.indexOf(rule),
          evalAttrs: rule.getEvalAttributes(),
          evalHoldouts,
          fallback,
          metadata
        });
      }
    }
    if (this.defaultRule) {
      const { abTest, variation, variationName } = await this.defaultRule.getRuleResult(
        this.key,
        this.salt,
        user.getIdentifier(this.evalByUserId)
      );
      return EvalResult.getDefaultRuleResult({
        variation,
        variationName,
        abTest,
        evalHoldouts,
        fallback,
        metadata
      });
    }
    return EvalResult.getFallbackResult("Evaluation failed");
  }
  isExperiment() {
    return this.kind === "abntest" || this.kind === "abtest";
  }
};

// package.json
var version = "9.0.3";

// src/types.ts
var REGIONS = {
  US: "us-client",
  EU: "eu-client",
  AP: "ap-client",
  ME: "me-client",
  SG: "sg-client",
  GLB: "client-api"
};
var LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3
};

// src/utils/errors.ts
var HttpError = class extends Error {
  message;
  status;
  constructor(message, status) {
    super(message);
    this.message = message;
    this.status = status;
  }
};

// src/http/HttpClient.ts
var HttpClient = class {
  constructor(config) {
    this.config = config;
    this.baseUrl = config.selfHostedUrl ?? `https://${REGIONS[config.region]}.fwf.deliveryhero.net`;
  }
  baseUrl;
  async getFeatures(features, testTarget, holdouts) {
    const response = await this.fetchWithRetry("v3/features-config/latest", {
      method: "PUT",
      body: JSON.stringify({
        features,
        holdouts,
        testTarget
      })
    });
    const arrayBuffer = await response.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  }
  async getVariation(featureKey, user) {
    const response = await this.fetchWithRetry(`v3/features/${featureKey}`, {
      method: "PUT",
      body: JSON.stringify({
        userId: user.authUserId,
        googleClientId: user.unAuthUserId,
        email: user.email,
        custom: user.custom
      })
    });
    const data = await response.json();
    return data;
  }
  async getVariations(featureKeys, user) {
    const response = await this.fetchWithRetry("v3/features", {
      method: "PUT",
      body: JSON.stringify({
        userId: user.authUserId,
        googleClientId: user.unAuthUserId,
        email: user.email,
        custom: user.custom,
        featureList: featureKeys
      })
    });
    const data = await response.json();
    return data;
  }
  async fetchWithRetry(path, options = {}, retries = this.config.requestRetries) {
    try {
      return await this.fetch(path, options);
    } catch (error) {
      if (retries > 1) {
        await new Promise((resolve) => setTimeout(resolve, 1e3));
        return this.fetchWithRetry(path, options, retries - 1);
      }
      throw error;
    }
  }
  async fetch(path, options) {
    const response = await fetch(`${this.baseUrl}/${path}`, {
      ...options,
      headers: {
        Authorization: this.config.accessToken,
        "Content-Type": "application/json",
        IsServer: typeof window === "undefined" ? "true" : "false",
        SdkInfo: `JsIsomorphic;sdk:${version}`,
        ...options.headers
      },
      signal: AbortSignal.timeout(this.config.requestTimeout)
    });
    if (!response.ok) {
      throw new HttpError("Failed to fetch", response.status);
    }
    return response;
  }
};

// src/logger/Logger.ts
var Logger = class _Logger {
  static prefix = "\u{1F6A9} [FwF SDK]";
  level;
  constructor({ level }) {
    this.level = LOG_LEVELS[level];
  }
  debug(message) {
    if (this.level <= LOG_LEVELS.DEBUG) {
      console.debug(_Logger.prefix, message);
    }
  }
  info(message) {
    if (this.level <= LOG_LEVELS.INFO) {
      console.info(_Logger.prefix, message);
    }
  }
  warn(message) {
    if (this.level <= LOG_LEVELS.WARN) {
      console.warn(_Logger.prefix, message);
    }
  }
  error(message) {
    if (this.level <= LOG_LEVELS.ERROR) {
      console.error(_Logger.prefix, message);
    }
  }
};

// src/models/User.ts
var User = class {
  authUserId;
  unAuthUserId;
  email;
  custom = {};
  constructor(renderer) {
    if (renderer) {
      this.custom = new Proxy(this.custom, {
        set: (target, key, value) => {
          target[key] = value;
          renderer();
          return true;
        },
        deleteProperty: (target, key) => {
          delete target[key];
          renderer();
          return true;
        }
      });
      return new Proxy(this, {
        set: (target, key, value) => {
          target[key] = value;
          renderer();
          return true;
        }
      });
    }
  }
  /**
   * Check if the user is valid
   * @returns {boolean}
   */
  isValid() {
    return !!this.authUserId || !!this.unAuthUserId;
  }
  /**
   * Get user identifier
   * @param evalByUserId
   * @returns {string}
   */
  getIdentifier(evalByUserId) {
    if (evalByUserId) {
      return this.authUserId ?? this.unAuthUserId ?? "";
    } else {
      return this.unAuthUserId ?? this.authUserId ?? "";
    }
  }
  /**
   * Get user custom attributes
   * @returns {Record<string, Attribute>}
   */
  getAttributes() {
    return this.custom;
  }
  /**
   * Set user attribute
   * @param key
   * @param value
   */
  setAttribute(key, value) {
    this.custom[key] = value;
  }
  /**
   * Set user attributes
   * @param attributes
   */
  setAttributes(attributes) {
    this.custom = { ...this.custom, ...attributes };
  }
  /**
   * Remove user attribute
   * @param key
   */
  removeAttribute(key) {
    delete this.custom[key];
  }
  /**
   * Get user as test target
   * @returns {TestTarget}
   */
  getTestTarget() {
    return {
      authUserId: this.authUserId,
      unAuthUserId: this.unAuthUserId,
      email: this.email
    };
  }
};

// src/client/Client.ts
var FwFClient = class {
  cache;
  backgroundUpdates;
  debugMode;
  eventTracking;
  featureKeys;
  featuresExpirationTime;
  httpClient;
  logger;
  user;
  sessionId;
  errorKeys = /* @__PURE__ */ new Set();
  interval;
  renderer = null;
  constructor(config) {
    this.backgroundUpdates = config.updateFeatures ?? true;
    this.debugMode = config.debugMode ?? false;
    this.featureKeys = config.featureKeys;
    this.featuresExpirationTime = config.featuresExpirationTime ?? 10 * 60 * 1e3;
    this.eventTracking = config.eventTracking;
    this.sessionId = config.sessionId;
    this.cache = new Cache({
      accessToken: config.accessToken,
      storage: config.storage ?? new LocalStorage()
    });
    this.httpClient = new HttpClient({
      accessToken: config.accessToken,
      region: config.region ?? "GLB",
      requestRetries: config.requestRetries ?? 0,
      requestTimeout: config.requestTimeout ?? 2e3,
      selfHostedUrl: config.selfHostedUrl
    });
    this.logger = new Logger({
      level: config.logLevel ?? "ERROR"
    });
    this.user = new User(() => this.render());
  }
  /**
   * Get the user object
   * @returns {User} The user object
   */
  getUser() {
    return this.user;
  }
  /**
   * Sync the client
   * @returns {Promise<boolean>} True if the sync process was successful, false otherwise
   */
  async sync() {
    if (this.debugMode) {
      this.logger.warn("Debug mode is enabled, please disable it in production environments");
    }
    this.logger.debug("Sync process has started");
    if (!await this.updateFeatures()) {
      this.logger.error("Failed to update features, background updates will remain disabled");
      return false;
    }
    if (this.backgroundUpdates) {
      clearInterval(this.interval);
      this.interval = setInterval(() => this.updateFeatures(), this.featuresExpirationTime);
    } else {
      this.logger.debug("Background updates are disabled");
    }
    this.logger.debug("Sync process has finished");
    return true;
  }
  /**
   * Get the variation of a feature with local evaluation
   * @param featureKey
   * @param fallback
   * @returns {Promise<EvalResult>} The evaluation result
   */
  async getVariation(featureKey, fallback) {
    if (!this.user.isValid()) {
      return EvalResult.getFallbackResult(fallback, "Invalid user");
    }
    try {
      const featureInCache = await this.cache.getFeature(featureKey);
      if (!featureInCache) {
        return EvalResult.getFallbackResult(fallback, "Feature was not found");
      }
      const feature = await Feature.fromMessage(featureInCache, this.cache);
      const evalResult = await feature.evaluate(this.user, fallback);
      this.trackEvalResult(featureKey, evalResult, this.user.authUserId, this.user.getIdentifier(feature.evalByUserId));
      return evalResult;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      return EvalResult.getFallbackResult(fallback, message);
    }
  }
  /**
   * Get the variations of multiple features with local evaluation
   * @param featureKeys
   * @param fallback
   * @returns {Promise<Record<T, EvalResult>>} The evaluation results
   */
  async getVariations(featureKeys, fallback) {
    const evalResults = {};
    const featuresInCache = await this.cache.getFeatures(featureKeys);
    for (const featureKey of featureKeys) {
      const featureInCache = featuresInCache[featureKey];
      if (featureInCache) {
        try {
          const feature = await Feature.fromMessage(featureInCache, this.cache);
          evalResults[featureKey] = await feature.evaluate(this.user, fallback[featureKey]);
          this.trackEvalResult(
            featureKey,
            evalResults[featureKey],
            this.user.authUserId,
            this.user.getIdentifier(feature.evalByUserId)
          );
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          evalResults[featureKey] = EvalResult.getFallbackResult(fallback[featureKey], message);
        }
      } else {
        evalResults[featureKey] = EvalResult.getFallbackResult(fallback[featureKey], "Feature was not found");
      }
    }
    return evalResults;
  }
  /**
   * Get the variation of a feature with server evaluation
   * @param featureKey
   * @param fallback
   * @returns {Promise<EvalResult>} The evaluation result
   */
  async getServerVariation(featureKey, fallback) {
    if (!this.user.isValid()) {
      return EvalResult.getFallbackResult(fallback, "Invalid user");
    }
    try {
      const response = await this.httpClient.getVariation(featureKey, this.user);
      const evalResult = EvalResult.getServerVariationResult(response, fallback);
      this.trackEvalResult(featureKey, evalResult);
      return evalResult;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      return EvalResult.getFallbackResult(fallback, message);
    }
  }
  /**
   * Get the variations of multiple features with server evaluation
   * @param featureKeys
   * @param fallback
   * @returns {Promise<Record<T, EvalResult>} The evaluation results
   */
  async getServerVariations(featureKeys, fallback) {
    const evalResults = {};
    if (!this.user.isValid()) {
      for (const featureKey of featureKeys) {
        evalResults[featureKey] = EvalResult.getFallbackResult(fallback[featureKey], "Invalid user");
      }
      return evalResults;
    }
    try {
      const response = await this.httpClient.getVariations(featureKeys, this.user);
      for (const featureKey of featureKeys) {
        evalResults[featureKey] = EvalResult.getServerVariationResult(response[featureKey], fallback[featureKey]);
        this.trackEvalResult(featureKey, evalResults[featureKey]);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      for (const featureKey of featureKeys) {
        evalResults[featureKey] = EvalResult.getFallbackResult(fallback[featureKey], message);
      }
    }
    return evalResults;
  }
  /**
   * Set a renderer function
   * @param renderer
   */
  setRenderer(renderer) {
    this.renderer = renderer;
  }
  render() {
    if (this.renderer) {
      this.renderer();
    }
  }
  async trackEvalResult(featureKey, evalResult, authUserId, experimentAllocationId) {
    let session = {};
    if (this.sessionId) {
      const sessionFromCache = await this.cache.getSession(this.sessionId);
      if (sessionFromCache) {
        session = sessionFromCache;
      }
    }
    const evalHoldouts = evalResult.explanation.evalHoldouts ?? {};
    for (const [holdoutKey, holdoutEvaluation] of Object.entries(evalHoldouts)) {
      if (!session[holdoutKey] || authUserId && authUserId !== session[holdoutKey]) {
        this.eventTracking?.onTrackHoldout(
          holdoutKey,
          holdoutEvaluation,
          evalResult.variation,
          featureKey,
          "holdout_group.participated"
        );
        if (authUserId) {
          session[holdoutKey] = authUserId;
        }
      }
    }
    if (evalResult.abTest) {
      if (!session[featureKey] || experimentAllocationId && experimentAllocationId !== session[featureKey]) {
        this.eventTracking?.onTrackExperiment(featureKey, evalResult.variation, "ab_test.participated");
        if (experimentAllocationId) {
          session[featureKey] = experimentAllocationId;
        }
      }
    }
    if (this.sessionId) {
      await this.cache.setSession(this.sessionId, session);
    }
  }
  async updateFeatures() {
    try {
      const featureKeysWithoutErrors = this.featureKeys.filter((featureKey) => !this.errorKeys.has(featureKey));
      const featureKeysVersions = await this.cache.getFeaturesToUpdate(
        featureKeysWithoutErrors,
        this.debugMode,
        this.featuresExpirationTime
      );
      if (!featureKeysVersions.length) {
        this.logger.debug("All features are up to date");
        return true;
      }
      const holdoutKeysInCache = await this.cache.getHoldoutKeys() ?? [];
      const testTarget = this.debugMode ? this.user.getTestTarget() : {};
      const response = await this.httpClient.getFeatures(featureKeysVersions, testTarget, holdoutKeysInCache);
      const { features, holdouts } = FeaturesMsg.fromBinary(response);
      const featuresToStore = {};
      for (const [featureKey, featureOrError] of Object.entries(features)) {
        if (featureOrError.error) {
          this.logger.error(`Failed to update feature ${featureKey}: ${featureOrError.error.message}`);
          this.errorKeys.add(featureKey);
          await this.cache.deleteFeature(featureKey);
        }
        if (featureOrError.feature) {
          featuresToStore[featureKey] = featureOrError.feature;
        }
      }
      await this.cache.setFeatures(featuresToStore);
      await this.cache.setHoldouts(holdouts);
      await this.cache.setHoldoutKeys([...holdoutKeysInCache, ...Object.keys(holdouts)]);
      await this.cache.setLastUpdatedAt();
      this.logger.debug("Features have been updated successfully");
      return true;
    } catch (error) {
      if (error instanceof HttpError) {
        if (error.status === 401) {
          this.logger.debug("Client is not authorized, background updates will be disabled");
          this.backgroundUpdates = false;
        }
      }
      const message = error instanceof Error ? error.message : "Unknown error";
      this.logger.error(`Failed to update features: ${message}`);
      return false;
    } finally {
      this.render();
    }
  }
};
export {
  FwFClient,
  IndexedDB,
  LocalStorage
};
